import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Tooltip } from "nlib/ui";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import React, { useCallback, useMemo, useState } from "react";
import TransactionAmount from "nlib/common/TransactionAmount";
import TransactionTypes from "const/TransactionTypes";
import classNames from "classnames";
import moment from "moment";

const TableRow = ({ header, document, entityKey, item, currency }) => {
  const { uiTexts } = useSelector(getTextsData);

  const [opened, setOpened] = useState(false);

  const handleMouseOver = useCallback(() => setOpened(true), []);

  const handleMouseLeave = useCallback(() => setOpened(false), []);

  const {
    amount,
    expense,
    timestamp,
    addressName,
    description,
    gptAiDescription,
    [entityKey]: entityValue
  } = useMemo(() => {
    if (document) {
      return {
        timestamp: document.issueDate,
        amount: document.amount,
        addressName: document.address?.name || "",
        expense: document.paymentType && document.paymentType === DocumentPaymentTypes.BUY
      };
    }

    return item || {};
  }, [item, document]);

  const content = useMemo(() => {
    if (!item && !document) {
      return [];
    }
    if (item) {
      return [
        { label: uiTexts[entityKey], value: entityValue },
        { label: uiTexts.bankStatementDetails, value: description },
        { label: uiTexts.aiDescription, value: gptAiDescription }
      ].filter(({ value }) => value);
    }

    const { paymentType, type } = document;

    const billType = paymentType === DocumentPaymentTypes.BUY && type === DocumentTypes.INVOICE;

    const typeContent = (paymentType && type && (billType ? DocumentTypes.BILL : type));

    return [
      { label: uiTexts.type, value: uiTexts[typeContent] || uiTexts.unknown },
      { label: uiTexts.documentNumber, value: document.originalDocumentId || uiTexts.unknown },
      {
        label: uiTexts.dueDate,
        value: document.dueDate ? moment.utc(document.dueDate).format(DateTimeFormats.DATE_TEXT) : uiTexts.unknown
      },
      {
        label: uiTexts.lineItems,
        value: (document.lineItems || []).map((lineItem) => lineItem.description).join(", ") || uiTexts.unknown
      }
    ].filter(({ value }) => value);
  }, [entityValue, document, entityKey, item, description, gptAiDescription, uiTexts]);

  if (header) {
    return (
      <div className={classNames(Css.row, Css.header)}>
        <div className={Css.cell}>{uiTexts.date}</div>
        <div className={Css.cell}>{uiTexts.amount}</div>
        <div className={Css.cell}>{uiTexts.payee}</div>
        <div className={classNames(Css.cell, Css.icon)} />
      </div>
    );
  }

  return (
    <div className={Css.row}>
      <div
        className={Css.cell}
        title={moment.utc(timestamp).format(DateTimeFormats.DATE_TEXT)}>
        {moment.utc(timestamp).format(DateTimeFormats.DATE_TEXT)}
      </div>
      <div className={Css.cell}>
        <TransactionAmount
          className={Css.amount}
          amount={amount}
          type={expense !== undefined
            && (expense ? TransactionTypes.WITHDRAW : TransactionTypes.DEPOSIT)}
          currency={currency} />
      </div>
      <div
        className={classNames(Css.cell, !addressName && Css.muted)}
        title={addressName}>
        {addressName || uiTexts.noPayee}
      </div>
      <div className={classNames(Css.cell, Css.icon)} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
        <Icons.Info />
      </div>
      <Tooltip opened={opened}>
        <div className={Css.tooltip}>
          {content.map(({ label, value }) => (
            <div key={label}>
              <strong>{label}: </strong>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </Tooltip>
    </div>
  );
};

export default React.memo(TableRow);
